import {
  IStyles,
  IconWrapper,
  QuantityButton,
  QuestionContainerWithSolution,
  TeacherUploadedTag,
  ActionPopUpSingleButton,
  cloneObject,
  QuestionSourceTags,
  deserify,
  getLocalStorage,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Question } from '@protos/content_management/content.db_pb';
import { Dispatch, SetStateAction, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../../reduxStore/reduxHooks';
import { setQuestionsCount } from '../../../reducer/assessmentFlow.slice';
import { interactionEvent } from '../../../../Auth/auth.events';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import {
  MarkingSchemeEnum,
  QuestionModuleMap,
} from '@protos/learning_management/lms.db_pb';
import { Timestamp } from '@bufbuild/protobuf';
import {
  isOmrQuestionLimitReached,
  OMR_MAX_QUESTIONS_LIMIT_MESSAGE,
} from '../../../utils/assessment.utils';

const styles: IStyles = {
  root: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: { md: '1px solid grey' },
    paddingBottom: pxTovW(20),
  },
  textBox: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: { xs: pxToRem(10), md: pxTovW(0) },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    '& > * ': { display: 'inline' },
  },
  questionWrapper: {
    margin: pxTovW(20),
    // width: { md: pxTovW(944) },
    position: 'relative',
    border: '1px solid #CED2FC',
    borderRadius: {
      xs: pxToRem(15),
      md: pxTovW(15),
    },
    marginBottom: { xs: pxToRem(20), md: pxTovW(20) },
    overflow: 'hidden',
  },
  questionPart: {
    padding: {
      xs: `${pxToRem(24)} ${pxToRem(11)}`,
      md: `${pxTovW(24)} ${pxTovW(50)}`,
    },
  },
  modal: {
    boxSizing: 'border-box',
    display: { xs: 'flex', md: 'block' },
    alignItems: 'flex-end',
  },

  modalRoot: {
    display: { xs: 'flex' },
    flexDirection: 'column',
    backgroundColor: 'common.white',
    margin: { md: 'auto' },
    width: { xs: '100%', md: pxTovW(300) },
    height: { xs: pxToRem(407), md: 'max-content' },
    maxHeight: { xs: pxToRem(407), md: '25.156vw' },
    borderRadius: { xs: `${pxToRem(30)} ${pxToRem(30)} 0 0`, md: pxToRem(15) },
    paddingY: pxToRem(22),
    paddingX: { md: pxToRem(22) },
    justifyContent: 'center',
    mt: { md: '30vh' },
    // alignItems: 'center',
  },
  headingBox: {
    borderBottom: '1px solid #E8E8E8',
    display: 'flex',
    justifyContent: 'space-between',

    padding: pxToRem(22),
    paddingTop: pxToRem(10),
    '&:hover': {
      backgroundColor: { md: '#ECFFF5' },
    },
  },
  closeBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
};

interface IProps {
  // questionList?: ModuleFilteredQuestions;
  questionList?: Question[];
  handleAddNewQuestion: (questionId: string, question: Question) => void;
  setQuestionBankDisplayFlag: Dispatch<SetStateAction<boolean>>;
  handleDeleteNewQuestion: (questionId: string) => void;
  selectedPage?: number;
  questionsPerPage?: number;
  fetchedQuestionModuleMap?: QuestionModuleMap[];
}
export default function QuestionsBank(props: IProps) {
  const {
    questionList,
    handleAddNewQuestion,
    setQuestionBankDisplayFlag,
    handleDeleteNewQuestion,
    selectedPage = 1,
    questionsPerPage = 20,
    fetchedQuestionModuleMap,
  } = props;
  const mediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { module_id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const user_info = useDeserifiedSelector(
    (state) => state.auth.user_info
  );
  const { isOffline } = useDownloadContext();
  const teacher_id = getLocalStorage('userId');
  const {
    module_filtered_questions,
    questions_count,
    assesment_creation_type,
  } = deserify(useAppSelector((state) => state.assessment));
  const { manage_mode_of_conduction } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const [omrQuestionLimitPopupOpen, setOmrQuestionLimitPopupOpen] =
    useState(false);

  // ------------------------------------------------------------

  const getModeOfConduction = () =>
    assesment_creation_type?.creationType === 'Readymade'
      ? manage_mode_of_conduction
      : assesment_creation_type?.modeOfConduction;

  // ------------------------------------------------------------

  const addQuestions = (questionId: string, question: Question) => {
    if (
      isOmrQuestionLimitReached(
        getModeOfConduction(),
        module_filtered_questions?.questions?.length ?? 0
      )
    ) {
      setOmrQuestionLimitPopupOpen(true);
      return;
    }
    handleAddNewQuestion(questionId, question);
    dispatch(setQuestionsCount());
  };

  // ------------------------------------------------------------

  const removeQuestions = (questionId: string) => {
    handleDeleteNewQuestion(questionId);
    dispatch(setQuestionsCount());
  };

  let questionIdSet: Set<string> | undefined;
  if (module_filtered_questions) {
    questionIdSet = new Set<string>(
      module_filtered_questions.questions.map((obj: any) => obj.questionId)
    );
  }
  const checkQuestions = (questionID: string | undefined) => {
    if (questionID) {
      if (questionIdSet && questionIdSet.has(questionID)) {
        return true;
      }
    }
    return false;
  };

  // ------------------------------------------------------------

  // function mapQuestionsByMarkingScheme(
  //   questionsArray: Question[],
  //   isJEEMarking: boolean
  // ): Question[] {
  //   const mappedQuestions = questionsArray.map((question) => {
  //     if (question?.question?.model?.value?.commonQuestionContent) {
  //       if (isJEEMarking) {
  //         question.question.model.value.commonQuestionContent.marks = [4];
  //       }
  //     }
  //     return new Question({ ...question });
  //   });
  //   return mappedQuestions;
  // }

  // ------------------------------------------------------------

  function mapQuestionsByMarkingScheme(
    questionsArray: Question[],
    markingScheme?: MarkingSchemeEnum,
    customMarks?: number
  ): Question[] {
    return questionsArray.map((question) => {
      const updatedQuestion = cloneObject(question);

      const commonQuestionContent =
        updatedQuestion?.question?.model?.value?.commonQuestionContent;

      if (commonQuestionContent) {
        if (markingScheme === MarkingSchemeEnum.MARKING_SCHEME_JEE) {
          commonQuestionContent.marks = [4];
        } else if (markingScheme === MarkingSchemeEnum.MARKING_SCHEME_CUSTOM) {
          commonQuestionContent.marks = [customMarks ?? 1];
        }
      }
      return new Question(updatedQuestion);
    });
  }

  // ------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box>
        <Box sx={styles.questionsContainer}>
          {questionList ? (
            mapQuestionsByMarkingScheme(
              questionList,
              assesment_creation_type.markingScheme,
              assesment_creation_type.marksPerQuestion
            ).map((ques, index: number) => (
              <Box
                sx={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  position: 'relative',
                }}
                key={ques.questionId || `ques_fallback_${index}`}
              >
                <QuestionContainerWithSolution
                  questionNumber={
                    (selectedPage - 1) * questionsPerPage + index + 1
                  }
                  headerLeftElement={
                    <QuestionSourceTags
                      question={ques}
                      teacherProfileId={teacher_id}
                      teacherName={user_info?.userName || ''}
                    />
                  }
                  headerRightElement={
                    !checkQuestions(ques.questionId) ? (
                      <Button
                        sx={{ marginLeft: 'auto' }}
                        onClick={async () => {
                          addQuestions(
                            ques.questionId || index.toString(),
                            ques
                          );
                          await interactionEvent({
                            context: 'question_bank',
                            name: 'ADD',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                      >
                        <QuantityButton quantity={0} maxQuantity={1} />
                      </Button>
                    ) : (
                      // <Typography
                      //   variant="h4"
                      //   color="secondary"
                      //   sx={{ marginLeft: 'auto' }}
                      // >
                      //   Question already added
                      // </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(10), md: pxTovW(20) },
                        }}
                      >
                        <Typography
                          variant="h5"
                          color="secondary.main"
                          fontWeight="bold"
                        >
                          ADDED
                        </Typography>
                        <Button
                          sx={{ marginLeft: 'auto', p: 0 }}
                          onClick={async () => {
                            ques.questionId && removeQuestions(ques.questionId);
                            await interactionEvent({
                              context: 'question_bank',
                              name: 'DELETE',
                              pathSegments: pathSegments,
                              isOffline: isOffline,
                            });
                          }}
                        >
                          <IconWrapper
                            name="delete2"
                            size="md"
                            parentFolder="icons"
                            type="png"
                            customSx={{
                              width: { xs: pxToRem(17), md: pxTovW(24) },
                              height: { xs: pxToRem(17), md: pxTovW(24) },
                            }}
                          />
                        </Button>
                      </Box>
                    )
                  }
                  isPreviouslyUsed={
                    fetchedQuestionModuleMap?.find(
                      (m) => m.questionId === ques.questionId
                    )?.isPreviouslyUsed
                  }
                  question={ques}
                  imageResizeEnabled={true}
                  showQuestionStats
                  showAnswer
                  hideBloomLevel={
                    assesment_creation_type.markingScheme ===
                    MarkingSchemeEnum.MARKING_SCHEME_JEE
                      ? true
                      : false
                  }
                />
              </Box>
            ))
          ) : (
            <Typography variant="h2">Loading...</Typography>
          )}
        </Box>
      </Box>

      <ActionPopUpSingleButton
        open={omrQuestionLimitPopupOpen}
        handleClose={() => setOmrQuestionLimitPopupOpen(false)}
        fontSmall
        iconName="error_yellow2"
        popupText={OMR_MAX_QUESTIONS_LIMIT_MESSAGE}
        ctaHandler={() => setOmrQuestionLimitPopupOpen(false)}
        buttontext="OK"
        background="rgba(193, 248, 218, 1)"
      />
    </Box>
  );
}
